/**
 * Milestone rollups — task↔ticket↔roadmap linking, computed.
 *
 * A RoadmapItem can link tickets (`linkedTicketIds`) and tasks
 * (`linkedTaskIds`). When ANY linked item resolves against the live
 * boards, the milestone's progress is auto-computed from completion
 * counts and the hand-set `progress` estimate is ignored — the roadmap
 * then reports reality, not a guess. With no (resolving) links, the
 * manual estimate keeps working exactly as before, so nothing breaks
 * for milestones that predate linking.
 *
 * WHY completion-count and not weighted/partial credit: the rollup has
 * to be explainable at a glance ("3 of 5 shipped → 60%"). Partial
 * credit for in-progress work makes the bar move for work that can
 * still slip, which is how roadmaps drift into fiction.
 *
 * WHY cancelled tickets are excluded entirely: abandoned work should
 * neither count as shipped nor hold a milestone hostage at <100%.
 */

import type { ProjectTask, RoadmapItem, Ticket } from '../db/types';

/** One linked ticket/task resolved against the live boards, chip-ready. */
export interface LinkedWorkRef {
  kind: 'ticket' | 'task';
  /** Ticket.id ("TIQ-012") or ProjectTask.id (uuid). */
  id: string;
  /** Ticket id+title or task title — what the chip renders. */
  label: string;
  /** Raw status, e.g. "in-progress". */
  status: string;
  done: boolean;
}

/** Auto-computed completion rollup for a milestone with linked work. */
export interface MilestoneRollup {
  refs: LinkedWorkRef[];
  /** Countable items (cancelled tickets excluded). */
  total: number;
  done: number;
  /** 0–100, floor()ed so a milestone never reads 100% early. */
  progress: number;
}

/**
 * Resolve a milestone's linked ids against the live tickets/tasks and
 * compute its completion. Returns null when nothing resolves (no links,
 * or every linked record was deleted) — callers then fall back to the
 * manual `progress` field.
 */
export function milestoneRollup(
  item: RoadmapItem,
  tickets: Ticket[],
  tasks: ProjectTask[],
): MilestoneRollup | null {
  const refs: LinkedWorkRef[] = [];

  for (const id of item.linkedTicketIds ?? []) {
    const t = tickets.find((x) => x.id === id);
    if (!t) continue; // deleted ticket — silently dropped from the rollup
    refs.push({
      kind: 'ticket',
      id: t.id,
      label: `${t.id} · ${t.title}`,
      status: t.status,
      done: t.status === 'done',
    });
  }

  for (const id of item.linkedTaskIds ?? []) {
    const t = tasks.find((x) => x.id === id);
    if (!t) continue;
    refs.push({
      kind: 'task',
      id: t.id,
      label: t.title,
      status: t.status,
      done: t.status === 'done',
    });
  }

  if (refs.length === 0) return null;

  const countable = refs.filter(
    (r) => !(r.kind === 'ticket' && r.status === 'cancelled'),
  );
  const done = countable.filter((r) => r.done).length;
  const total = countable.length;
  // All-cancelled edge case: show the refs but report 0/0 → 0%.
  const progress = total === 0 ? 0 : Math.floor((done / total) * 100);

  return { refs, total, done, progress };
}

/**
 * The single progress number a milestone card should render: the auto
 * rollup when links resolve, otherwise the hand-set estimate (clamped).
 */
export function milestoneProgress(
  item: RoadmapItem,
  rollup: MilestoneRollup | null,
): number {
  if (rollup) return rollup.progress;
  return Math.max(0, Math.min(100, item.progress ?? 0));
}

/** Every milestone that rolls up a given ticket — for the ticket drawer. */
export function milestonesLinkingTicket(
  items: RoadmapItem[],
  ticketId: string,
): RoadmapItem[] {
  return items.filter((it) => (it.linkedTicketIds ?? []).includes(ticketId));
}

/** Every milestone that rolls up a given task — for the task drawer. */
export function milestonesLinkingTask(
  items: RoadmapItem[],
  taskId: string,
): RoadmapItem[] {
  return items.filter((it) => (it.linkedTaskIds ?? []).includes(taskId));
}
